<template>
  <div class="drawer-overlay">
    <aside class="drawer">
      <div class="drawer-title-row">
        <div>
          <h2>Notifications</h2>
          <p class="muted">Operational alerts and assigned work.</p>
        </div>
        <div class="notification-drawer-actions">
          <button
            class="btn drawer-close"
            type="button"
            aria-label="Close notifications"
            @click="emit('close')"
          >
            ×
          </button>
        </div>
      </div>

      <div class="tabs notification-type-tabs" role="tablist" aria-label="Notification type">
        <button
          id="attention-tab"
          class="tab"
          :class="{ active: activeSection === 'attention' }"
          type="button"
          role="tab"
          :aria-selected="activeSection === 'attention'"
          aria-controls="attention-panel"
          :tabindex="activeSection === 'attention' ? 0 : -1"
          @click="activeSection = 'attention'"
          @keydown="selectAdjacentTab($event)"
        >
          Needs attention
          <span class="notification-count">{{ notifications.attentionUnreadCount }}</span>
        </button>
        <button
          id="delivery-tab"
          class="tab"
          :class="{ active: activeSection === 'delivery' }"
          type="button"
          role="tab"
          :aria-selected="activeSection === 'delivery'"
          aria-controls="delivery-panel"
          :tabindex="activeSection === 'delivery' ? 0 : -1"
          @click="activeSection = 'delivery'"
          @keydown="selectAdjacentTab($event)"
        >
          Delivery history <span class="notification-count">{{ unreadNotifications.length }}</span>
        </button>
      </div>

      <section
        v-if="activeSection === 'attention'"
        id="attention-panel"
        class="attention-section"
        role="tabpanel"
        aria-labelledby="attention-tab"
        tabindex="0"
      >
        <div class="section-heading-row">
          <h3>Needs attention</h3>
          <button
            class="btn sm"
            type="button"
            :disabled="notifications.attentionUnreadCount === 0 || notifications.attentionLoading"
            @click="notifications.markAllAttentionRead()"
          >
            Mark all attention read
          </button>
        </div>
        <div
          v-if="notifications.attentionLoading && notifications.attentionItems.length === 0"
          class="callout"
        >
          Loading attention alerts...
        </div>
        <div v-else-if="notifications.attentionErrorMessage" class="callout danger">
          {{ notifications.attentionErrorMessage }}
          <button class="btn sm" type="button" @click="notifications.loadAttentionNotifications()">
            Try again
          </button>
        </div>
        <div v-else-if="notifications.attentionItems.length === 0" class="callout">
          No work needs attention.
        </div>
        <div
          v-for="notification in notifications.attentionItems"
          :key="notification.notificationId"
          class="attention-row"
          :class="{ unread: !notification.isRead }"
        >
          <button class="attention-open" type="button" @click="openAttention(notification)">
            <span class="attention-title">{{ notification.subject }}</span>
            <span class="tiny muted">
              {{ notificationEventLabel(notification.eventType) }} ·
              {{ formatMerchantDate(notification.createdAt) }}
            </span>
          </button>
          <button
            v-if="!notification.isRead"
            class="btn sm"
            type="button"
            :aria-label="`Mark ${notification.subject} read`"
            @click="notifications.markAttentionRead(notification.notificationId)"
          >
            Mark read
          </button>
          <span v-else class="badge b-gray">Read</span>
        </div>
        <button
          v-if="notifications.attentionNextCursor"
          class="btn"
          type="button"
          :disabled="notifications.attentionLoading"
          @click="notifications.loadAttentionNotifications(true)"
        >
          {{ notifications.attentionLoading ? "Loading..." : "Load more" }}
        </button>
      </section>

      <section
        v-else
        id="delivery-panel"
        class="delivery-section"
        role="tabpanel"
        aria-labelledby="delivery-tab"
        tabindex="0"
      >
        <div class="section-heading-row">
          <h3>Delivery history</h3>
          <button
            class="btn sm"
            type="button"
            :disabled="unreadNotifications.length === 0 || notifications.loading"
            @click="notifications.markAllRead()"
          >
            Mark all delivery read
          </button>
        </div>
        <label class="notification-filter">
          Show
          <select v-model="deliveryFilter" aria-label="Filter delivery history">
            <option value="all">All deliveries</option>
            <option value="unread">Unread deliveries</option>
          </select>
        </label>

        <div v-if="notifications.loading" class="callout" style="margin-top: 14px">
          Loading notifications...
        </div>
        <div v-else-if="notifications.errorMessage" class="callout danger" style="margin-top: 14px">
          {{ notifications.errorMessage }}
        </div>
        <div v-else-if="visibleNotifications.length === 0" class="callout" style="margin-top: 14px">
          No notifications to show.
        </div>
        <template v-else>
          <div
            v-for="notification in visibleNotifications"
            :key="notification.notificationId"
            class="notification-row"
            :class="{ unread: notification.status !== 'READ' }"
          >
            <div>
              <b>{{ notification.subject }}</b>
              <p class="muted">{{ notification.bodyText }}</p>
              <div class="tiny muted">
                {{ notificationEventLabel(notification.eventType) }} ·
                {{ notificationTimestampLabel(notification) }}
              </div>
            </div>
            <button
              v-if="notification.status !== 'READ'"
              class="btn sm"
              type="button"
              @click="notifications.markRead(notification.notificationId)"
            >
              Mark read
            </button>
            <span v-else class="badge" :class="statusClass(notification.status)">{{
              notification.status
            }}</span>
          </div>
        </template>
      </section>

      <div class="callout notification-safety-note">
        Notifications are store-scoped and never include raw provider payloads, credentials, or
        delivery secrets.
      </div>
    </aside>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";

import { formatMerchantDate } from "../../../features/view-model-formatters.js";
import type { MerchantAttentionNotification } from "../models.js";
import { notificationEventLabel, notificationTimestampLabel } from "../formatters.js";
import { defaultNotificationFilters, useNotificationsStore } from "../store.js";
import type { MerchantNotification, NotificationStatus } from "../models.js";

const emit = defineEmits<{
  close: [];
}>();

const notifications = useNotificationsStore();
const router = useRouter();
const activeSection = ref<"attention" | "delivery">("attention");
const deliveryFilter = ref<"all" | "unread">("all");

const unreadNotifications = computed(() =>
  notifications.notifications.filter((notification) => notification.status !== "READ"),
);
const visibleNotifications = computed(() =>
  deliveryFilter.value === "unread" ? unreadNotifications.value : notifications.notifications,
);

onMounted(() => {
  void notifications.loadAll({ ...defaultNotificationFilters(), limit: 50 });
});

async function openAttention(notification: MerchantAttentionNotification): Promise<void> {
  delete notifications.attentionErrorMessage;
  await notifications.markAttentionRead(notification.notificationId);
  if (
    notifications.attentionErrorMessage !== undefined ||
    notification.actionUrl === undefined ||
    !notification.actionUrl.startsWith("/") ||
    notification.actionUrl.startsWith("//")
  )
    return;
  emit("close");
  await router.push(notification.actionUrl);
}

function statusClass(status: MerchantNotification["status"] | NotificationStatus): string {
  if (status === "FAILED") return "b-red";
  if (status === "PENDING") return "b-orange";
  if (status === "SENT") return "b-green";
  return "b-gray";
}

function selectAdjacentTab(event: KeyboardEvent): void {
  if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
  event.preventDefault();
  activeSection.value = activeSection.value === "attention" ? "delivery" : "attention";
  const tabId = activeSection.value === "attention" ? "attention-tab" : "delivery-tab";
  document.getElementById(tabId)?.focus();
}
</script>

<style scoped>
.notification-drawer-actions {
  align-items: center;
  display: flex;
  gap: 10px;
}

.notification-type-tabs {
  margin-top: 18px;
}

.notification-count {
  align-items: center;
  background: var(--pr-surface-subtle, #f2f4f7);
  border-radius: 999px;
  display: inline-flex;
  font-size: 12px;
  font-weight: 700;
  height: 22px;
  justify-content: center;
  margin-left: 5px;
  min-width: 22px;
  padding: 0 6px;
}

.attention-section {
  margin-top: 18px;
}

.section-heading-row {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}

.section-heading-row h3 {
  margin: 0;
}

.attention-row {
  align-items: center;
  border-bottom: 1px solid var(--pr-border, #e2e8f0);
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding: 12px 4px;
}

.attention-row.unread {
  background: var(--pr-surface-subtle, #f8fafc);
}

.attention-open {
  background: transparent;
  border: 0;
  color: inherit;
  cursor: pointer;
  display: grid;
  gap: 5px;
  text-align: left;
}

.attention-title {
  font-weight: 600;
}

.delivery-section {
  margin-top: 18px;
}

.notification-filter {
  align-items: center;
  display: flex;
  gap: 8px;
  margin: 0 0 12px;
}

.notification-filter select {
  background: #ffffff;
  border: 1px solid var(--pr-border, #d0d5dd);
  border-radius: 8px;
  color: inherit;
  font: inherit;
  padding: 7px 10px;
}

.notification-safety-note {
  margin-top: 18px;
}
</style>
